import { computed, inject, ref, watch } from 'vue';
import moment from 'moment';
import { app } from '@/bootloader';
import { Dashboard_Filter, Dashboard_InitiatorType, Dashboard_WorkloadType } from '@/graphql/codegen/generated/graphql';
import { IKApplicationPersistentData } from '@/services/persistentDataProvider/persistentDataProvider.types';
import { IKDashboardFilters } from './dashboard-filter.injectionKeys';

export const useDashboardFiltersProvider = () => {
  type DashboardPagePersistentDataType = {
    filter: Dashboard_Filter;
  };

  const dashboardPersistentDataSection = 'DashboardPage';

  const appPersistentData = inject(IKApplicationPersistentData);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const rangeDate = ref({ from: moment().subtract(6, 'days').startOf('day'), to: moment().endOf('day') });
  const initiatorType = ref<Dashboard_InitiatorType | null>(null);
  const workloadType = ref<Dashboard_WorkloadType | null>(null);
  const buildGroup = ref<string | null>(null);
  const isDirty = computed(() => initiatorType.value !== null || workloadType.value !== null || buildGroup.value !== null);

  watch(
    () => appPersistentData?.GQL?.networkStatus?.value,
    () => {
      const { filter } = appPersistentData?.getPersistentData(dashboardPersistentDataSection) as DashboardPagePersistentDataType;
      if (filter && Object.keys(filter).length > 0) {
        rangeDate.value = { from: moment(filter?.from), to: moment(filter?.to) };
        initiatorType.value = filter.initiatorType ? filter.initiatorType : null;
        workloadType.value = filter.workloadType ? filter.workloadType : null;
        buildGroup.value = filter.buildGroup ? filter.buildGroup : null;
      }
    },
    {
      immediate: true,
    },
  );

  const clearFilters = () => {
    rangeDate.value = { from: moment().subtract(7, 'days'), to: moment() };
    initiatorType.value = null;
    workloadType.value = null;
    buildGroup.value = null;
  };

  const setPersistentDashboardFilter = () => {
    const filter = {
      from: rangeDate.value.from.valueOf(),
      to: rangeDate.value.to.valueOf(),
      initiatorType: initiatorType.value,
      workloadType: workloadType.value,
      buildGroup: buildGroup.value,
      timeZone,
    };
    appPersistentData?.setPersistentData(dashboardPersistentDataSection, { filter } as DashboardPagePersistentDataType);
  };

  const payload = {
    setWorkloadFilter: (value: Dashboard_WorkloadType) => {
      workloadType.value = value;
      setPersistentDashboardFilter();
    },
    setInitiatorFilter: (value: Dashboard_InitiatorType) => {
      initiatorType.value = value;
      setPersistentDashboardFilter();
    },
    setBuildGroupFilter: (value: string | null) => {
      buildGroup.value = value;
      setPersistentDashboardFilter();
    },
    setRangeDateFilter: (value: { from: moment.Moment; to: moment.Moment }) => {
      rangeDate.value = value;
      setPersistentDashboardFilter();
    },
    clearFilters,
    isDirty,
    rangeDate,
    initiatorType,
    workloadType,
    buildGroup,
    timeZone,
  };

  app.provide(IKDashboardFilters, payload);
  return payload;
};

export const useDashboardFilters = () => {
  const dashboardFilters = inject(IKDashboardFilters);
  if (!dashboardFilters) throw new Error('Dashboard filters not provided');
  return dashboardFilters;
};
